import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Activity, Download, AlertTriangle, Info, HelpCircle } from 'lucide-react';
import { Bar, Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  Plugin
} from 'chart.js';
import {
  OpenRouterService,
  GapAnalysisResult,
  GapCluster,
  GapAnalysisSpeakerBaseline,
} from '../services/openRouterService';
import { useAuth } from '../services/AuthContext';
import { useAudioSeek } from '../contexts/AudioContext';
import { SpeakerLabels } from '../types';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

interface GapAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  speakerLabels: SpeakerLabels;
  gapAnalysisData?: GapAnalysisResult | null;
  ttmlFiles?: any[];
  selectedTtmlId?: string | null;
  onFilteredClustersChange?: (clusters: GapCluster[], nonClusteredGaps?: Array<{ startTime: number; gapDuration: number; zScore: number; speaker: number }>) => void;
}

const SEVERITY_COLORS: Record<string, string> = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#22c55e',
};

const SEVERITY_ICONS: Record<string, string> = {
  high: '\u{1F534}',
  medium: '\u{1F7E1}',
  low: '\u{1F7E2}',
};

const SIGNAL_LABELS: Record<string, string> = {
  'high-hesitation': 'Frequent Pauses',
  'sustained-hesitation': 'Sustained Pauses',
  'minor-hesitation': 'Minor Pauses',
  // Legacy labels (cached gap analysis data may still have old keys)
  'novel-but-rough': 'Frequent Pauses',
  'confused-or-uncertain': 'Frequent Pauses',
  'hesitant-clear-audio': 'Frequent Pauses',
  'hesitant-unclear-audio': 'Frequent Pauses',
  'energy-dip': 'Sustained Pauses',
};

// Speaker color palette
const SPEAKER_COLORS = [
  { bg: 'rgba(59, 130, 246, 0.7)', border: 'rgb(59, 130, 246)', dot: 'rgb(59, 130, 246)' },
  { bg: 'rgba(239, 68, 68, 0.7)', border: 'rgb(239, 68, 68)', dot: 'rgb(239, 68, 68)' },
  { bg: 'rgba(34, 197, 94, 0.7)', border: 'rgb(34, 197, 94)', dot: 'rgb(34, 197, 94)' },
  { bg: 'rgba(168, 85, 247, 0.7)', border: 'rgb(168, 85, 247)', dot: 'rgb(168, 85, 247)' },
];

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Extract topic boundaries from the active TTML file */
function extractTopicBoundaries(ttmlFiles: any[], selectedTtmlId: string | null): Array<{ time: number; label: string }> {
  if (!ttmlFiles || !selectedTtmlId) return [];
  const ttmlFile = ttmlFiles.find((f: any) => f.checksum === selectedTtmlId);
  if (!ttmlFile) return [];

  // Try to extract topics from the TTML content
  const topics = ttmlFile.topics || [];
  if (topics.length === 0) return [];

  return topics.map((t: any, i: number) => ({
    time: t.startTime || t.start || 0,
    label: t.title || t.name || `Topic ${i + 1}`
  })).filter((t: { time: number }) => t.time > 0);
}

export default function GapAnalysisModal({ isOpen, onClose, sessionId, speakerLabels: rawSpeakerLabels, gapAnalysisData, ttmlFiles, selectedTtmlId, onFilteredClustersChange }: GapAnalysisModalProps) {
  // Convert numeric-keyed SpeakerLabels to string-keyed for API compatibility
  const speakerLabels: Record<string, string> = {};
  if (rawSpeakerLabels) {
    for (const [k, v] of Object.entries(rawSpeakerLabels)) {
      speakerLabels[String(k)] = v;
    }
  }
  const { token } = useAuth();
  const { seekTo } = useAudioSeek();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<GapAnalysisResult | null>(null);
  const [showZScoreInfo, setShowZScoreInfo] = useState(false);

  // Slider state
  const [gapThreshold, setGapThreshold] = useState(0.5);
  const [zScoreCutoff, setZScoreCutoff] = useState(1.5);

  // Chart refs for updates
  const histogramRef = useRef<any>(null);
  const timelineRef = useRef<any>(null);

  // Use pre-loaded data if available
  useEffect(() => {
    if (isOpen && gapAnalysisData && !data) {
      setData(gapAnalysisData);
    }
  }, [isOpen, gapAnalysisData]);

  // Run analysis when modal opens if no data
  useEffect(() => {
    if (isOpen && !data && !loading && !gapAnalysisData) {
      runAnalysis();
    }
  }, [isOpen]);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await OpenRouterService.analyzeGaps(
        sessionId,
        { minGapSeconds: 0.15, relativeThreshold: 1.0, clusterMaxGap: 30, windowSize: 300 },
        token || undefined
      );
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to analyze gaps');
    } finally {
      setLoading(false);
    }
  }, [sessionId, token]);

  // Force chart updates when thresholds change
  useEffect(() => {
    if (histogramRef.current) {
      histogramRef.current.update();
    }
    if (timelineRef.current) {
      timelineRef.current.update();
    }
  }, [gapThreshold, zScoreCutoff]);

  // Filter clusters by both gap threshold and z-score cutoff
  // This mirrors the backend: changing minGapSeconds eliminates shorter gaps,
  // which can shrink or eliminate clusters. We recompute cluster stats from
  // qualifying gaps so the preview matches what the TTML download will contain.
  const filteredClusters = useMemo(() => {
    if (!data) return [];
    const result: GapCluster[] = [];
    for (const cluster of data.clusters) {
      if (!cluster.gaps) {
        // Fallback for clusters without gap detail — use original avgZScore
        if (cluster.avgZScore >= zScoreCutoff) result.push(cluster);
        continue;
      }
      // Filter this cluster's gaps by both thresholds
      const qualifyingGaps = cluster.gaps.filter(
        g => g.gapDuration >= gapThreshold && g.zScore >= zScoreCutoff
      );
      // Need at least 2 qualifying gaps to form a cluster (matches backend minGapsInCluster)
      if (qualifyingGaps.length < 2) continue;
      // Recompute cluster stats from qualifying gaps
      const avgZ = qualifyingGaps.reduce((s, g) => s + g.zScore, 0) / qualifyingGaps.length;
      const maxZ = Math.max(...qualifyingGaps.map(g => g.zScore));
      let severity: 'high' | 'medium' | 'low';
      if (avgZ >= 2.5) severity = 'high';
      else if (avgZ >= 1.5) severity = 'medium';
      else severity = 'low';

      result.push({
        ...cluster,
        gapCount: qualifyingGaps.length,
        avgZScore: Math.round(avgZ * 100) / 100,
        maxZScore: Math.round(maxZ * 100) / 100,
        severity,
        gaps: qualifyingGaps,
      });
    }
    return result.sort((a, b) => b.avgZScore - a.avgZScore);
  }, [data, gapThreshold, zScoreCutoff]);

  // Compute non-clustered gaps (qualifying gaps not inside any cluster)
  const nonClusteredGaps = useMemo(() => {
    if (!data) return [];
    const clusteredKeys = new Set<string>();
    for (const c of filteredClusters) {
      if (!c.gaps) continue;
      for (const g of c.gaps) clusteredKeys.add(`${g.startTime}_${g.speaker}`);
    }
    return data.gaps
      .filter(g => g.gapDuration >= gapThreshold && g.zScore >= zScoreCutoff && !clusteredKeys.has(`${g.startTime}_${g.speaker}`));
  }, [data, filteredClusters, gapThreshold, zScoreCutoff]);

  // Propagate filtered clusters + non-clustered gaps to parent (debounced to avoid lag while dragging sliders)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!onFilteredClustersChange) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      onFilteredClustersChange(filteredClusters, nonClusteredGaps);
    }, 300);
    return () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); };
  }, [filteredClusters, nonClusteredGaps, onFilteredClustersChange]);

  // Compute filtered gap count
  const filteredGapCount = useMemo(() => {
    if (!data) return 0;
    return data.gaps.filter(g => g.gapDuration >= gapThreshold && g.zScore >= zScoreCutoff).length;
  }, [data, gapThreshold, zScoreCutoff]);

  // Most gappy speaker
  const mostGappySpeaker = useMemo(() => {
    if (!data) return null;
    const baselines = Object.values(data.speakerBaselines) as GapAnalysisSpeakerBaseline[];
    if (baselines.length === 0) return null;
    return baselines.reduce((a, b) => a.meanGap > b.meanGap ? a : b);
  }, [data]);

  // Gappiness index (average z-score of significant gaps)
  const gappinessIndex = useMemo(() => {
    if (!data || data.gaps.length === 0) return 0;
    const sig = data.gaps.filter(g => g.gapDuration >= gapThreshold && g.zScore >= zScoreCutoff);
    if (sig.length === 0) return 0;
    return (sig.reduce((s, g) => s + g.zScore, 0) / sig.length).toFixed(1);
  }, [data, gapThreshold, zScoreCutoff]);

  // Topic boundaries from active TTML
  const topicBoundaries = useMemo(() => {
    return extractTopicBoundaries(ttmlFiles || [], selectedTtmlId || null);
  }, [ttmlFiles, selectedTtmlId]);

  // Compute edit entries from ALL qualifying gaps (clustered + non-clustered)
  // This matches the TTML download which includes every gap above both thresholds
  const editEntries = useMemo(() => {
    if (!data) return [];
    const seen = new Set<string>();
    const entries: Array<{ startTime: number; endTime: number; zScore: number; speaker: string }> = [];

    function addGap(g: any) {
      const key = `${g.startTime}_${g.speaker}`;
      if (seen.has(key)) return;
      if (g.gapDuration < gapThreshold || g.zScore < zScoreCutoff) return;
      seen.add(key);
      entries.push({
        startTime: g.startTime,
        endTime: g.startTime + g.gapDuration,
        zScore: g.zScore,
        speaker: String(g.speaker),
      });
    }

    // Gaps from filtered clusters
    for (const c of filteredClusters) {
      if (!c.gaps) continue;
      for (const g of c.gaps) addGap(g);
    }

    // Non-clustered gaps from the full gaps array
    for (const g of data.gaps) addGap(g);

    return entries.sort((a, b) => a.startTime - b.startTime);
  }, [data, filteredClusters, gapThreshold, zScoreCutoff]);

  // Compute clip regions (clean 3-min windows avoiding gappy clusters)
  const clipRegions = useMemo(() => {
    if (!data) return [];
    const totalDuration = data.summary.totalDuration;
    const avoidRanges = filteredClusters.map(c => [c.startTime, c.endTime]);
    const isClean = (start: number, end: number) =>
      !avoidRanges.some(([s, e]) => start < e && end > s);

    const regions: Array<{ startTime: number; endTime: number; label: string }> = [];
    for (let t = 60; t < totalDuration - 180; t += 300) {
      if (isClean(t, t + 180)) {
        regions.push({ startTime: t, endTime: t + 180, label: 'Clean segment' });
        if (regions.length >= 3) break;
      }
    }
    return regions;
  }, [data, filteredClusters]);

  // TTML download counts — what the user will get at current slider values
  const ttmlCounts = useMemo(() => ({
    summarySegments: filteredClusters.length,
    edits: editEntries.length,
    clips: clipRegions.length + 1, // +1 for alignment placeholder
  }), [filteredClusters, editEntries, clipRegions]);

  // Histogram chart data
  const chartData = useMemo(() => {
    if (!data || !data.histogram.bins) return null;
    const bins = data.histogram.bins;

    // Get unique speaker labels
    const allSpeakers = new Set<string>();
    for (const bin of bins) {
      for (const sp of Object.keys(bin.bySpeaker)) {
        allSpeakers.add(sp);
      }
    }
    const speakerList = Array.from(allSpeakers);

    const thresholdBinIdx = bins.findIndex(b => b.binEnd >= gapThreshold);

    const datasets = speakerList.map((speaker, idx) => {
      const color = SPEAKER_COLORS[idx % SPEAKER_COLORS.length];
      return {
        label: speaker,
        data: bins.map((bin) => bin.bySpeaker[speaker] || 0),
        backgroundColor: bins.map((_, i) =>
          i >= thresholdBinIdx && thresholdBinIdx >= 0
            ? color.bg.replace('0.7', '0.9')
            : color.bg
        ),
        borderColor: color.border,
        borderWidth: 1,
      };
    });

    return {
      labels: bins.map(b => b.label),
      datasets,
    };
  }, [data, gapThreshold]);

  const chartOptions: ChartOptions<'bar'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y} gaps`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: 'Gap Duration' },
        ticks: { maxTicksLimit: 12 },
      },
      y: {
        stacked: true,
        title: { display: true, text: 'Count' },
        beginAtZero: true,
      },
    },
  }), []);

  // Threshold line plugin — uses ref to always access current gapThreshold
  const gapThresholdRef = useRef(gapThreshold);
  gapThresholdRef.current = gapThreshold;

  const thresholdPlugin: Plugin<'bar'> = useMemo(() => ({
    id: 'thresholdLine',
    afterDraw: (chart: any) => {
      if (!data) return;
      const binWidth = data.histogram.binWidth;
      if (!binWidth) return;
      const currentThreshold = gapThresholdRef.current;
      const binIndex = currentThreshold / binWidth;
      const xScale = chart.scales.x;
      const yScale = chart.scales.y;
      if (!xScale || !yScale) return;

      const xPos = xScale.getPixelForValue(binIndex);
      const ctx = chart.ctx;
      ctx.save();
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(xPos, yScale.top);
      ctx.lineTo(xPos, yScale.bottom);
      ctx.stroke();

      // Label
      ctx.fillStyle = '#f59e0b';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${currentThreshold.toFixed(2)}s`, xPos, yScale.top - 4);

      ctx.restore();
    },
  }), [data]);

  // Timeline chart data — gaps above threshold plotted over episode time
  const timelineData = useMemo(() => {
    if (!data || !data.gaps) return null;

    const filteredGaps = data.gaps.filter(g => g.gapDuration >= gapThreshold && g.zScore >= zScoreCutoff);
    if (filteredGaps.length === 0) return null;

    // Group by speaker
    const allSpeakers = new Set<string>();
    filteredGaps.forEach(g => allSpeakers.add(String(g.speaker)));
    const speakerList = Array.from(allSpeakers);

    const datasets = speakerList.map((speaker, idx) => {
      const color = SPEAKER_COLORS[idx % SPEAKER_COLORS.length];
      const speakerGaps = filteredGaps.filter(g => String(g.speaker) === speaker);
      const speakerLabel = speakerLabels[speaker] || data.speakerBaselines[speaker]?.label || `Speaker ${speaker}`;

      return {
        label: speakerLabel,
        data: speakerGaps.map(g => ({
          x: g.startTime,
          y: g.zScore,
        })),
        backgroundColor: color.dot,
        borderColor: color.border,
        pointRadius: 4,
        pointHoverRadius: 6,
      };
    });

    return { datasets };
  }, [data, gapThreshold, zScoreCutoff, speakerLabels]);

  // Ref for seekTo so chart onClick can use it without causing re-renders
  const seekToRef = useRef(seekTo);
  seekToRef.current = seekTo;

  const timelineOptions: ChartOptions<'scatter'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { bottom: 20, left: 30 }, // Space for edit/clip lines and labels
    },
    onClick: (_event: any, elements: any[], chart: any) => {
      if (elements.length > 0) {
        const el = elements[0];
        const point = chart.data.datasets[el.datasetIndex].data[el.index];
        if (point && typeof point.x === 'number') {
          seekToRef.current(point.x);
        }
      }
    },
    onHover: (event: any, elements: any[]) => {
      if (event.native?.target) {
        event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    },
    plugins: {
      legend: { position: 'top' as const, labels: { boxWidth: 8, padding: 8 } },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const point = ctx.raw;
            return `${ctx.dataset.label}: z=${point.y.toFixed(1)} at ${formatTime(point.x)} (click to seek)`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'linear' as const,
        title: { display: true, text: 'Episode Time' },
        ticks: {
          callback: (value: any) => formatTime(value),
          maxTicksLimit: 10,
        },
        min: 0,
        max: data?.summary?.totalDuration || undefined,
      },
      y: {
        title: { display: true, text: 'Z-Score' },
        beginAtZero: true,
      },
    },
  }), [data]);

  // Topic boundary vertical lines plugin for timeline chart
  const topicBoundaryPlugin: Plugin<'scatter'> = useMemo(() => ({
    id: 'topicBoundaries',
    afterDraw: (chart: any) => {
      if (!topicBoundaries.length) return;
      const xScale = chart.scales.x;
      const yScale = chart.scales.y;
      if (!xScale || !yScale) return;
      const ctx = chart.ctx;

      for (const boundary of topicBoundaries) {
        const xPos = xScale.getPixelForValue(boundary.time);
        if (xPos < xScale.left || xPos > xScale.right) continue;

        ctx.save();
        ctx.strokeStyle = 'rgba(148, 163, 184, 0.5)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(xPos, yScale.top);
        ctx.lineTo(xPos, yScale.bottom);
        ctx.stroke();

        // Topic label (rotated)
        ctx.fillStyle = 'rgba(148, 163, 184, 0.7)';
        ctx.font = '9px sans-serif';
        ctx.save();
        ctx.translate(xPos + 3, yScale.top + 10);
        ctx.rotate(-Math.PI / 4);
        const shortLabel = boundary.label.length > 20 ? boundary.label.substring(0, 18) + '...' : boundary.label;
        ctx.fillText(shortLabel, 0, 0);
        ctx.restore();
        ctx.restore();
      }
    },
  }), [topicBoundaries]);

  // Cluster shading plugin for timeline chart
  const clusterShadingPlugin: Plugin<'scatter'> = useMemo(() => ({
    id: 'clusterShading',
    beforeDatasetsDraw: (chart: any) => {
      if (!filteredClusters.length) return;
      const xScale = chart.scales.x;
      const yScale = chart.scales.y;
      if (!xScale || !yScale) return;
      const ctx = chart.ctx;

      for (const cluster of filteredClusters) {
        const x1 = xScale.getPixelForValue(cluster.startTime);
        const x2 = xScale.getPixelForValue(cluster.endTime);
        const color = cluster.severity === 'high' ? 'rgba(239, 68, 68, 0.08)'
          : cluster.severity === 'medium' ? 'rgba(245, 158, 11, 0.08)'
          : 'rgba(234, 179, 8, 0.06)';

        ctx.save();
        ctx.fillStyle = color;
        ctx.fillRect(x1, yScale.top, x2 - x1, yScale.bottom - yScale.top);
        ctx.restore();
      }
    },
  }), [filteredClusters]);

  // Refs for edit/clip data so plugins can access current values
  const editEntriesRef = useRef(editEntries);
  editEntriesRef.current = editEntries;
  const clipRegionsRef = useRef(clipRegions);
  clipRegionsRef.current = clipRegions;
  const filteredClustersRef = useRef(filteredClusters);
  filteredClustersRef.current = filteredClusters;

  // Edit & clip horizontal lines plugin for timeline chart
  const editClipLinesPlugin: Plugin<'scatter'> = useMemo(() => ({
    id: 'editClipLines',
    afterDatasetsDraw: (chart: any) => {
      const xScale = chart.scales.x;
      const yScale = chart.scales.y;
      if (!xScale || !yScale) return;
      const ctx = chart.ctx;
      const bottom = yScale.bottom;

      // Draw edit cluster bars (red/amber) — solid horizontal bars at bottom
      const clusters = filteredClustersRef.current;
      if (clusters.length > 0) {
        for (const cluster of clusters) {
          const x1 = Math.max(xScale.getPixelForValue(cluster.startTime), xScale.left);
          const x2 = Math.min(xScale.getPixelForValue(cluster.endTime), xScale.right);
          if (x2 <= x1) continue;
          const color = cluster.severity === 'high' ? 'rgba(239, 68, 68, 0.8)'
            : cluster.severity === 'medium' ? 'rgba(245, 158, 11, 0.7)'
            : 'rgba(234, 179, 8, 0.6)';

          ctx.save();
          ctx.fillStyle = color;
          ctx.fillRect(x1, bottom - 8, x2 - x1, 4);
          ctx.restore();
        }

        // Label
        ctx.save();
        ctx.fillStyle = 'rgba(239, 68, 68, 0.7)';
        ctx.font = '9px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText('Edits', xScale.left - 4, bottom - 5);
        ctx.restore();
      }

      // Draw clip region bars (blue) — solid horizontal bars below edits
      const clips = clipRegionsRef.current;
      if (clips.length > 0) {
        for (const clip of clips) {
          const x1 = Math.max(xScale.getPixelForValue(clip.startTime), xScale.left);
          const x2 = Math.min(xScale.getPixelForValue(clip.endTime), xScale.right);
          if (x2 <= x1) continue;

          ctx.save();
          ctx.fillStyle = 'rgba(59, 130, 246, 0.6)';
          ctx.fillRect(x1, bottom - 16, x2 - x1, 4);
          ctx.restore();
        }

        // Label
        ctx.save();
        ctx.fillStyle = 'rgba(59, 130, 246, 0.7)';
        ctx.font = '9px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText('Clips', xScale.left - 4, bottom - 13);
        ctx.restore();
      }
    },
  }), []);

  const handleDownloadXml = () => {
    const url = OpenRouterService.getGapTtmlDownloadUrl(sessionId, {
      minGapSeconds: gapThreshold,
      relativeThreshold: zScoreCutoff,
    });
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = 'gap-analysis.ttml';
        link.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(err => setError(`Download failed: ${err.message}`));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Activity className="h-5 w-5 mr-2 text-amber-500" />
              Gap Analysis
            </h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1 p-4 space-y-4">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-amber-500 mr-3" />
                <span className="text-gray-600 dark:text-gray-300">Analyzing speech gaps...</span>
              </div>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-700 dark:text-red-300 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {error}
                </p>
                <button onClick={runAnalysis} className="mt-2 text-sm text-red-600 hover:underline">
                  Retry
                </button>
              </div>
            )}

            {data && !loading && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard label="Total Gaps" value={String(data.summary.totalGaps)} sub={`${filteredGapCount} above threshold`} />
                  <StatCard label="Clusters" value={String(filteredClusters.length)} sub={`of ${data.summary.clusters} total`} />
                  <StatCard
                    label="Most Gappy"
                    value={mostGappySpeaker?.label || '-'}
                    sub={mostGappySpeaker ? `${Math.round(mostGappySpeaker.meanGap * 1000)}ms avg` : ''}
                  />
                  <StatCard label="Gappiness" value={String(gappinessIndex)} sub="avg z-score" />
                </div>

                {/* Z-Score Explainer */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <button
                    onClick={() => setShowZScoreInfo(!showZScoreInfo)}
                    className="flex items-center gap-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 w-full"
                  >
                    <HelpCircle className="h-4 w-4" />
                    What is a Z-score?
                    <span className="ml-auto text-xs">{showZScoreInfo ? 'Hide' : 'Show'}</span>
                  </button>
                  {showZScoreInfo && (
                    <div className="mt-2 text-sm text-blue-600 dark:text-blue-400 space-y-1">
                      <p>Z-score measures how unusual a gap is <strong>for that specific speaker</strong>. It compares each gap against the speaker's own baseline.</p>
                      <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                        <div className="bg-blue-100 dark:bg-blue-900/40 rounded p-1.5 text-center">
                          <div className="font-bold">z = 1.0</div>
                          <div>Slightly unusual</div>
                        </div>
                        <div className="bg-blue-100 dark:bg-blue-900/40 rounded p-1.5 text-center">
                          <div className="font-bold">z = 2.0</div>
                          <div>Very unusual</div>
                        </div>
                        <div className="bg-blue-100 dark:bg-blue-900/40 rounded p-1.5 text-center">
                          <div className="font-bold">z = 3.0+</div>
                          <div>Extreme outlier</div>
                        </div>
                      </div>
                      <p className="text-xs mt-1">A fast speaker who suddenly pauses for 2 seconds will have a high z-score even if a slow speaker pauses for 2 seconds routinely (low z-score). This is why relative gappiness matters more than absolute gap duration.</p>
                    </div>
                  )}
                </div>

                {/* Histogram */}
                {chartData && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Gap Duration Distribution
                    </h4>
                    <div style={{ height: '220px' }}>
                      <Bar ref={histogramRef} data={chartData} options={chartOptions} plugins={[thresholdPlugin]} />
                    </div>
                  </div>
                )}

                {/* Timeline Chart */}
                {timelineData && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Gap Timeline
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        Dots = gaps | Bottom bars: <span style={{ color: 'rgb(239, 68, 68)' }}>edits</span> / <span style={{ color: 'rgb(59, 130, 246)' }}>clips</span>
                        {topicBoundaries.length > 0 && ' | Dashed = topics'}
                      </span>
                    </h4>
                    <div style={{ height: '230px' }}>
                      <Scatter
                        ref={timelineRef}
                        data={timelineData}
                        options={timelineOptions}
                        plugins={[topicBoundaryPlugin, clusterShadingPlugin, editClipLinesPlugin]}
                      />
                    </div>
                  </div>
                )}

                {/* Sliders */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300 mb-1">
                      <span>Gap Threshold</span>
                      <span className="font-mono font-semibold">{gapThreshold.toFixed(2)}s</span>
                    </label>
                    <input
                      type="range"
                      min="0.1"
                      max="3.0"
                      step="0.05"
                      value={gapThreshold}
                      onChange={(e) => setGapThreshold(parseFloat(e.target.value))}
                      className="w-full accent-amber-500"
                    />
                  </div>
                  <div>
                    <label className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300 mb-1">
                      <span>Z-Score Cutoff</span>
                      <span className="font-mono font-semibold">{zScoreCutoff.toFixed(1)}</span>
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="4.0"
                      step="0.1"
                      value={zScoreCutoff}
                      onChange={(e) => setZScoreCutoff(parseFloat(e.target.value))}
                      className="w-full accent-amber-500"
                    />
                  </div>

                  {/* TTML Download Preview Counts */}
                  <div className="flex items-center gap-4 pt-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">TTML Preview:</span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'rgba(245, 158, 11, 0.8)' }} />
                      {ttmlCounts.summarySegments} {ttmlCounts.summarySegments === 1 ? 'region' : 'regions'}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'rgba(239, 68, 68, 0.8)' }} />
                      {ttmlCounts.edits} {ttmlCounts.edits === 1 ? 'edit' : 'edits'}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'rgba(59, 130, 246, 0.8)' }} />
                      {ttmlCounts.clips} {ttmlCounts.clips === 1 ? 'clip' : 'clips'}
                    </span>
                    <span className="ml-auto text-gray-400 dark:text-gray-500">
                      {filteredGapCount} gaps total
                    </span>
                  </div>
                </div>

                {/* Per-Speaker Breakdown */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Per-Speaker Breakdown
                  </h4>
                  <div className="space-y-3">
                    {Object.values(data.speakerBaselines).map((b: GapAnalysisSpeakerBaseline) => {
                      const speakerGaps = data.gaps.filter(g => String(g.speaker) === b.speakerId);
                      const aboveThreshold = speakerGaps.filter(g => g.gapDuration >= gapThreshold && g.zScore >= zScoreCutoff).length;
                      const cleanPct = speakerGaps.length > 0
                        ? Math.round(((speakerGaps.length - aboveThreshold) / speakerGaps.length) * 100)
                        : 100;
                      const avgZ = aboveThreshold > 0
                        ? (speakerGaps.filter(g => g.gapDuration >= gapThreshold && g.zScore >= zScoreCutoff).reduce((s, g) => s + g.zScore, 0) / aboveThreshold).toFixed(1)
                        : '0';

                      return (
                        <div key={b.speakerId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {b.label}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {b.wordsPerMinute} WPM, median {Math.round(b.medianGap * 1000)}ms
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            {aboveThreshold} gaps above threshold, avg z={avgZ}
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="h-2 rounded-full transition-all"
                              style={{
                                width: `${cleanPct}%`,
                                backgroundColor: cleanPct >= 70 ? '#22c55e' : cleanPct >= 40 ? '#f59e0b' : '#ef4444'
                              }}
                            />
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {cleanPct}% of speech is clean
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Gappy Regions (Clusters) */}
                {filteredClusters.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Gappy Regions ({filteredClusters.length})
                    </h4>
                    <div className="space-y-2">
                      {filteredClusters.map((c: GapCluster, idx: number) => {
                        const speakerNames = c.speakers
                          .map(s => speakerLabels[s] || data.speakerBaselines[s]?.label || `Speaker ${s}`)
                          .join(', ');
                        return (
                          <div
                            key={idx}
                            className="border-l-4 pl-3 py-2"
                            style={{ borderColor: SEVERITY_COLORS[c.severity] || '#94a3b8' }}
                          >
                            <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                              <span>{SEVERITY_ICONS[c.severity] || ''}</span>
                              <span>[{formatTime(c.startTime)}-{formatTime(c.endTime)}]</span>
                              <span>{c.gapCount} gaps, z={c.avgZScore}</span>
                              <span className="text-gray-500">{speakerNames}</span>
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 flex items-center gap-1">
                              <span className="font-semibold uppercase" style={{ color: SEVERITY_COLORS[c.severity] }}>
                                {SIGNAL_LABELS[c.editorialSignal] || c.editorialSignal}
                              </span>
                              {c.editorialDescription && (
                                <span>— {c.editorialDescription}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {filteredClusters.length === 0 && data.summary.clusters > 0 && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-sm text-green-700 dark:text-green-300 flex items-center">
                    <Info className="h-4 w-4 mr-2 flex-shrink-0" />
                    No clusters at current z-score cutoff ({zScoreCutoff}). Try lowering the threshold to see more regions.
                  </div>
                )}

                {/* Compact Summary (collapsible) */}
                {data.compactSummary && (
                  <details className="bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <summary className="p-3 text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
                      Prompt Summary (injected into TTML generation)
                    </summary>
                    <pre className="p-3 pt-0 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono overflow-x-auto">
                      {data.compactSummary}
                    </pre>
                  </details>
                )}
              </>
            )}
          </div>

          {/* Footer Actions */}
          {data && !loading && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex gap-3 flex-shrink-0">
              <button
                onClick={handleDownloadXml}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                <Download className="h-4 w-4 mr-2" />
                Download TTML
              </button>
              <button
                onClick={runAnalysis}
                className="flex items-center px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
              >
                <Activity className="h-4 w-4 mr-2" />
                Re-analyze
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Stat card sub-component
function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
      <div className="text-xl font-bold text-gray-900 dark:text-white">{value}</div>
      {sub && <div className="text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
    </div>
  );
}
